/**
 * Quick-edit forms: each widget's one to three most used fields, for the
 * card attached to it on the canvas. Everything else is in "All settings".
 */
import { Stack } from "@duro-app/ui";

import {
  ICON_OPTIONS,
  IconSetting,
  SegmentedSetting,
  TextSetting,
  useProps,
  type Choice,
  type ItemRef,
} from "./controls.js";

const BUTTON_STYLES: readonly Choice<string>[] = [
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
  { value: "link", label: "Link" },
];
const LINK_STYLES: readonly Choice<string>[] = [
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
];

export function ButtonQuick(item: ItemRef): React.ReactElement {
  const linked = typeof useProps(item)["href"] === "string";
  return (
    <Stack gap="sm">
      <TextSetting item={item} prop="label" label="Label" />
      {linked ? (
        <TextSetting item={item} prop="href" label="Link" type="url" />
      ) : (
        <SegmentedSetting
          item={item}
          prop="variant"
          label="Style"
          choices={BUTTON_STYLES}
          fallback="primary"
        />
      )}
      {linked && (
        <SegmentedSetting
          item={item}
          prop="variant"
          label="Style"
          choices={LINK_STYLES}
          fallback="primary"
        />
      )}
    </Stack>
  );
}

export function InputQuick(item: ItemRef): React.ReactElement {
  const grouped = typeof useProps(item)["icon"] === "string";
  return (
    <Stack gap="sm">
      <TextSetting item={item} prop="label" label="Label" />
      <TextSetting item={item} prop="placeholder" label="Placeholder" />
      {grouped && (
        <IconSetting
          item={item}
          prop="icon"
          label="Icon"
          choices={ICON_OPTIONS}
          fallback="search"
        />
      )}
    </Stack>
  );
}

const LEVELS: readonly Choice<"1" | "2" | "3" | "4">[] = [
  { value: "1", label: "H1" },
  { value: "2", label: "H2" },
  { value: "3", label: "H3" },
  { value: "4", label: "H4" },
];

export function HeadingQuick(item: ItemRef): React.ReactElement {
  return (
    <SegmentedSetting
      item={item}
      prop="level"
      label="Level"
      choices={LEVELS}
      fallback="2"
      toProp={(v) => Number(v)}
    />
  );
}

const CARD_STYLES: readonly Choice<string>[] = [
  { value: "outlined", label: "Outlined" },
  { value: "elevated", label: "Elevated" },
  { value: "filled", label: "Filled" },
];

export function CardQuick(item: ItemRef): React.ReactElement {
  return (
    <SegmentedSetting
      item={item}
      prop="variant"
      label="Style"
      choices={CARD_STYLES}
      fallback="outlined"
    />
  );
}

export function IconQuick(item: ItemRef): React.ReactElement {
  return (
    <IconSetting
      item={item}
      prop="name"
      label="Icon"
      choices={ICON_OPTIONS}
      fallback="search"
    />
  );
}
